<!-- @prettier -->
<template>
	<ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }" slim>
		<form
			role="form"
			class="form-horizontal"
			data-testid="NewRingTreeForm"
			@submit.prevent="handleSubmit(onSubmit)"
		>
			<fieldset>
				<!-- id -->
				<Field
					v-if="currentRingTree.id"
					:label="$t('PING_TREE_ID')"
					:has-tooltip="false"
				>
					<template #element>
						<div class="col-sm-6 col-md-4 p-t-2 p-b-10">
							<div
								v-clipboard="{ copyText: currentRingTree.id }"
								v-tooltip="'Copy'"
								placement="right"
								class="btn btn-link m-l-5 p-0 no_border"
							>
								<i class="typcn typcn-tabs-outline" />
							</div>
							{{ currentRingTree.id }}
						</div>
					</template>
				</Field>

				<!-- name required -->
				<Field
					:label="$t('PING_TREE_NAME')"
					:has-tooltip="true"
					:tooltip-text="$t('PING_TREE_NAME_TT')"
				>
					<template #element>
						<ValidationProvider
							v-slot="{ classes }"
							ref="name"
							rules="required"
							slim
						>
							<div :class="classes">
								<div class="col-xs-6 col-sm-4">
									<input
										v-model="currentRingTree.name"
										type="text"
										name="name"
										autocomplete="off"
										class="form-control"
										custom-autofocus="true"
									/>
								</div>
								<label
									v-t="'REQUIRED'"
									class="col-sm-3 col-xs-2 form-group__label-required"
								/>
							</div>
						</ValidationProvider>
					</template>
				</Field>

				<!-- TimeZone -->
				<Field
					:label="$t('PING_TREE_TIME_ZONE')"
					:has-tooltip="true"
					:tooltip-text="$t('PING_TREE_TIME_ZONE_TT')"
				>
					<template #element>
						<ValidationProvider
							v-slot="{ classes }"
							ref="subId"
							rules="required"
							slim
						>
							<div :class="classes">
								<div class="col-xs-6 col-sm-4">
									<SearchableDropdown
										v-model="currentRingTree.schedule.timeZoneId"
										:config="timeZoneOptions"
										class="rtb-export-timezone m-r-15 m-b-15"
										:clearable="true"
										data-testid="timezone-dropdown"
									/>
								</div>
								<label
									v-t="'REQUIRED'"
									class="col-sm-3 col-xs-2 form-group__label-required"
								/>
							</div>
						</ValidationProvider>
					</template>
				</Field>

				<!-- hoursOfOperation -->
				<Field
					:label="$t('PING_TREE_SET_HOURS')"
					:has-tooltip="true"
					:tooltip-text="$t('PING_TREE_SET_HOURS_TT')"
				>
					<template #element>
						<div class="col-xs-4 col-sm-2 display-flex flex--align-center">
							<NzToggle
								v-model="hoursOfOperationEnabled"
								name="hoursOfOperation"
							/>
							<label
								v-if="!hoursOfOperationEnabled"
								v-t="'PING_TREES_SET_HOURS_STATUS'"
								class="m-l-15 m-b-0"
							/>
						</div>
					</template>
				</Field>

				<HoursOfOperation
					v-if="hoursOfOperationEnabled"
					:initialHoursOfOperation="computedHoursOfOperation"
					@hoursOfOperation="updateRingTreeHoursOfOperation"
				/>
				<!-- hoursOfOperation End -->

				<!-- Revenue Settings -->
				<div>
					<legend class="col-lg-offset-1 col-lg-10 display-flex">
						<span>{{ $t('PING_TREE_REVENUE_SETTINGS_TITLE') }}</span>
						<a
							v-tooltip.hover.right="$t('PING_TREE_REVENUE_SETTINGS_TITLE_TT')"
							class="m-l-10 f-s-16"
						>
							<i class="fa fa-question-circle"></i>
						</a>
					</legend>

					<!-- Minimum Revenue Amount -->
					<Field
						:label="$t('PING_TREE_REVENUE_MIN_TO_ROUTE_AMOUNT')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_REVENUE_MIN_TO_ROUTE_AMOUNT_TT')"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes, errors }"
								ref="minRevenueToRoute"
								rules="required|min_value:0"
								slim
							>
								<div :class="classes">
									<div class="col-sm-9">
										<span class="currency">$</span>
										<input
											v-model="currentRingTree.minRevenueToRoute"
											type="number"
											step="0.01"
											class="form-control currency input-sm"
											data-testid="currentRingTree.minRevenueToRoute"
											@wheel="$event.target.blur()"
										/>
										<label
											v-if="errors[0]"
											class="form-group__label-required vv-invalid"
										>{{ $t('ERROR_PING_TREE_REVENUE_MIN_TO_ROUTE_AMOUNT_TT') }}
										</label>
											<label v-else
											v-t="'REQUIRED'"
											class="m-l-20 form-group__label-required"
										></label>
									</div>
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<!-- Ring Tree Revenue Type -->
					<Field
						:label="$t('PING_TREE_REVENUE_TYPE')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_REVENUE_TYPE_TT')"
						class="m-b-5"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes }"
								ref="revenueTypeSettings"
								rules=""
								slim
							>
								<div :class="classes">
									<div class="col-sm-9">
										<MultipleToggle
											:options="revenueTypeOptions"
											:value="currentRingTree.revenueSettings.revenueDynamic"
											@onToggle="onRevenueTypeSettingToggled"
										/>
									</div>
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<!-- Static Revenue Amount -->
					<Field
						v-if="!currentRingTree.revenueSettings.revenueDynamic"
						:label="$t('PING_TREE_STATIC_REVENUE_AMOUNT')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_STATIC_REVENUE_AMOUNT_TT')"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes, errors }"
								rules="required|min_value:0"
								slim
							>
								<div :class="classes">
									<div class="col-sm-9">
										<span class="currency">$</span>
										<input
											v-model="currentRingTree.revenueSettings.staticRevenueAmount"
											type="number"
											step="0.01"
											class="form-control currency input-sm"
											@wheel="$event.target.blur()"
										/>
							  	<label
										v-t="'REQUIRED'"
										class="m-l-20 form-group__label-required"
									/>
									</div>
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<!-- Failure Revenue Amount -->
					<Field
						:label="$t('PING_TREE_REVENUE_ON_FAILURE_AMOUNT')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_REVENUE_ON_FAILURE_AMOUNT')"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes, errors }"
								rules="required|min_value:0"
								slim
							>
								<div :class="classes">
									<div class="col-sm-9">
										<span class="currency">$</span>
										<input
											v-model="currentRingTree.revenueSettings.revenueOnFailure"
											type="number"
											step="0.01"
											class="form-control currency input-sm"
											data-testid="currentRingTree.revenueSettings.revenueOnFailure"
											@wheel="$event.target.blur()"
										/>
										<label
											v-if="errors[0]"
											class="form-group__label-required vv-invalid"
										>{{ $t('ERROR_PING_TREE_REVENUE_ON_FAILURE_AMOUNT') }}
										</label>
											<label v-else
											v-t="'REQUIRED'"
											class="m-l-20 form-group__label-required"
										></label>
									</div>		
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<div>
						<div class="form-group">
							<!-- Convert On -->
							<Field
								:label="$t('PING_TREE_CONVERT')"
								:has-tooltip="true"
								:tooltip-text="$t('PING_TREE_CONVERT_TT')"
								class="p-b-0"
							>
								<template #element>
									<ValidationProvider
										v-slot="{ classes }"
										ref="conversionType"
										rules="required"
										slim
									>
										<div :class="classes">
											<div class="col-xs-4 col-lg-3">
												<SearchableDropdown
													v-model="
														currentRingTree.revenueSettings.conversionParams
															.conversionType
													"
													:config="revenueConvertOnSettingConfig"
													:clearable="true"
													class="m-r-15 m-b-15"
												/>
											</div>
											<label
												v-t="'REQUIRED'"
												class="col-sm-3 col-md-3 form-group__label-required"
											/>
										</div>
									</ValidationProvider>
								</template>
							</Field>
						</div>
						<div v-if="currentRingTree.revenueSettings.revenueDynamic && computedConversionType === 'postBack'" class="form-group p-t-0">
							<div class="col-sm-offset-3 col-sm-5">
								<div class="bs-callout bs-callout-sm bs-callout-warning m-t-0 m-b-0">{{ $t('PING_TREE_REVENUE_SETTINGS_WARNING_MESSAGE') }}</div>
							</div>
						</div>
						<template v-if="computedConversionType === 'callLength'">
							<!-- Start Call length on -->
							<Field
								:label="$t('PING_TREE_START_EVENT')"
								:has-tooltip="true"
								:tooltip-text="$t('PING_TREE_START_EVENT_TT')"
								class="m-b-5"
							>
								<template #element>
									<ValidationProvider
										v-slot="{ classes }"
										ref="startCallLengthOn"
										rules=""
										slim
									>
										<div :class="classes">
											<div class="ol-sm-9 col-md-9">
												<MultipleToggle
													:options="startCallLengthOnOptions"
													:value="
														currentRingTree.revenueSettings.conversionParams
															.conversionArgs.startTimerEvent
													"
													@onToggle="startCallLengthOnToggled"
												/>
											</div>
										</div>
									</ValidationProvider>
								</template>
							</Field>

							<!-- Conversion Call length value -->
							<Field
								:label="$t('PING_TREE_TARGET_DYNAMIC_LENGTH')"
								:has-tooltip="true"
								:tooltip-text="$t('PING_TREE_TARGET_DYNAMIC_LENGTH_TT')"
								class="m-b-5"
							>
								<template #element>
									<ValidationProvider
										v-slot="{ classes }"
										ref="conversionCallLengthValue"
										rules=""
										slim
									>
										<div :class="classes">
											<div class="ol-sm-9 col-md-9">
												<MultipleToggle
													:options="conversionCallLengthValueOptions"
													:value="
														currentRingTree.revenueSettings
															.conversionCallLengthDynamic
													"
													@onToggle="conversionCallLengthValueToggled"
												/>
											</div>
										</div>
									</ValidationProvider>
								</template>
							</Field>

							<!-- Length when conversion length is static -->
							<Field
								v-if="!currentRingTree.revenueSettings.conversionCallLengthDynamic"
								:label="$t('PING_TREE_TARGET_LENGTH')"
								:has-tooltip="true"
								:tooltip-text="$t('PING_TREE_TARGET_LENGTH_TT')"
							>
								<template #element>
									<ValidationProvider
										v-slot="{ classes }"
										rules="required"
										slim
									>
										<div :class="classes">
											<div class="col-sm-2 col-md-1">
												<input
													v-model="currentRingTree.revenueSettings.conversionParams.conversionArgs.callLengthInSeconds"
													type="number"
													step="0.01"
													class="form-control input-sm"
													@wheel="$event.target.blur()"
												/>
											</div>
											<label
												v-t="'REQUIRED'"
												class="col-sm-3 col-md-3 form-group__label-required"
											/>
										</div>
									</ValidationProvider>
								</template>
							</Field>

							<!-- length for dynamic conversion -->
							<Field
								v-if="currentRingTree.revenueSettings.conversionCallLengthDynamic"
								:label="$t('PING_TREE_TARGET_DYNAMIC_MAX_LENGTH')"
								:has-tooltip="true"
								:tooltip-text="$t('PING_TREE_TARGET_DYNAMIC_MAX_LENGTH_TT')"
							>
								<template #element>
									<ValidationProvider v-slot="{ classes }" rules="" slim>
										<div :class="classes">
											<div class="col-xs-12 col-sm-6 col-md-3">
												<input
													v-model="currentRingTree.revenueSettings.maxConversionCallDurationSeconds"
													type="number"
													class="form-control m-b-0 input-md"
													placeholder="unlimited"
													@wheel="$event.target.blur()"
												/>
												<span v-t="'SECONDS_TEXT'"></span>
											</div>
										</div>
									</ValidationProvider>
								</template>
							</Field>
						</template>
					</div>
					<!-- finish -->
				</div>

				<!-- Error Settings Start -->
				<div>
					<legend class="col-lg-offset-1 col-lg-10">
						<span>{{ $t('PING_TREE_ERROR_SETTINGS_TITLE') }}</span>
						<a
							v-tooltip.hover.right="$t('PING_TREE_ERROR_SETTINGS_TITLE_TT')"
							class="m-l-10 f-s-16"
						>
							<i class="fa fa-question-circle"></i>
						</a>
					</legend>

					<!-- Dial Timeout -->
					<Field
						:label="$t('PING_TREE_DIAL_TIMEOUT_SECONDS')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_DIAL_TIMEOUT_SECONDS_TT')"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes, errors }"
								rules="required|min_value:5|max_value:300"
								slim
							>
								<div :class="classes">
									<div class="col-xs-12 col-sm-9 col-md-9">
										<input
											v-model="currentRingTree.errorSettings.dialTimeoutSeconds"
											type="number"
											class="form-control m-b-0 input-sm"
											data-testid="currentRingTree.errorSettings.dialTimeoutSeconds"
											placeholder="10"
											@wheel="$event.target.blur()"
										/>
										<span v-t="'SECONDS_TEXT'"></span>
										<label
											v-if="errors[0]"
											class="form-group__label-required vv-invalid"
										>{{ $t('ERROR_PING_TREE_DIAL_TIMEOUT_SECONDS') }}
                  </label>
										<label v-else
										v-t="'REQUIRED'"
										class="m-l-20 form-group__label-required"
									></label>
									</div>
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<!-- Ping Timeout -->
					<Field
						:label="$t('PING_TREE_PING_TIMEOUT_MILLISECONDS')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_PING_TIMEOUT_MILLISECONDS_TT')"
					>
						<template #element>
							<ValidationProvider
								v-slot="{ classes, errors }"
								rules="required|min_value:50|max_value:10000"
								slim
							>
								<div :class="classes">
									<div class="col-xs-12 col-sm-9 col-md-9">
										<input
											v-model="currentRingTree.errorSettings.pingTimeoutInMS"
											type="number"
											class="form-control m-b-0 input-sm"
											data-testid="currentRingTree.errorSettings.pingTimeoutInMS"
											placeholder="2000"
											@wheel="$event.target.blur()"
										/>
										<span v-t="'MILLISECONDS_TEXT'"></span>
										<label
											v-if="errors[0]"
											class="form-group__label-required vv-invalid"
										>{{ $t('ERROR_PING_TREE_PING_TIMEOUT_MILLISECONDS') }}
                  </label>
									<label v-else
										v-t="'REQUIRED'"
										class="m-l-20 form-group__label-required"
									></label>
									</div>
								</div>
							</ValidationProvider>
						</template>
					</Field>

					<!-- Call on Failure -->
					<Field
						:label="$t('PING_TREE_CALL_ON_FAILURE')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_CALL_ON_FAILURE_TT')"
					>
						<template #element>
							<div class="col-xs-4 col-sm-2 col-md-2">
								<NzToggle
									v-model="currentRingTree.errorSettings.callOnFailure"
									name="callOnFailure"
								/>
							</div>
						</template>
					</Field>
				</div>
				<!-- Error Settings End -->

				<!-- Add Ring Tree Targets Start -->
				<div>
					<legend class="col-lg-offset-1 col-lg-10">
						<span>{{ $t('PING_TREE_ADD_TARGETS_TITLE') }}</span>
						<a
							v-tooltip.hover.right="$t('PING_TREE_ADD_TARGETS_TITLE_TT')"
							class="m-l-10 f-s-16"
						>
							<i class="fa fa-question-circle"></i>
						</a>
					</legend>

          <div v-if="!hasRingTreeTargets" class="col-lg-offset-1 col-lg-10">
						<div class="m-t-0 bs-callout bs-callout-warning">
					   	<p>{{ $t('PING_TREE_ADD_TARGETS_REQUIRED') }}</p>
					  </div>
					</div>
				

					<!-- select target -->
					<Field
						:label="$t('PING_TREE_SELECT_TARGET')"
						:has-tooltip="true"
						:tooltip-text="$t('PING_TREE_SELECT_TARGET_TT')"
					>
						<template #element>
							<div class="col-xs-4 col-lg-3">
								<SearchableDropdown
									v-model="selectedTarget"
									:config="targetsConfig"
									:clearable="true"
									:searchable="true"
									class="m-r-15 m-b-15"
									data-testid="select-target-dropdown"
								/>
							</div>
							<div class="col-xs-2 col-sm-3">
								<button
									class="btn btn-success"
									type="button"
									:disabled="targetInCurrentTree"
									@click="addTargetToTree"
									data-testid="add-target-to-tree"
								>
									{{ $t('PING_TREE_LIST_ADD') }}
								</button>
							</div>
						</template>
					</Field>
				</div>
				<!-- table view Ring Tree Target -->
				<div
					v-if="hasRingTreeTargets"
					class="m-t-10 col-md-offset-3 col-md-6 m-b-20"
				>
					<v-table
						:data="currentRingTree.targets"
						:hide-sort-icons="true"
						class="table_beauty"
					>
						<thead slot="head">
							<tr>
								<v-th v-t="'PING_TREE_LIST_NAME'" sort-key="name" />
								<v-th
									v-t="'PING_TREE_LIST_DESTINATION'"
									sort-key="instructions.number"
								/>
								<th />
							</tr>
						</thead>
						<tbody slot="body" slot-scope="{ displayData }">
							<template v-for="target in displayData">
								<tr>
									<td>
										<a
											:href="navigateToTarget(target.id)"
											class="table-ellipsis__name linked-name"
											target="_blank"
											:title="target.name"
										>
											{{ target.name }}
										</a>
									</td>
									<td v-if="target.isNumberDynamic">
										<span>{{ $t('PING_TREE_DYNAMIC_TARGET_NUMBER') }}</span>
									</td>
									<td v-if="!target.isNumberDynamic">
										{{ target.instructions.number }}
									</td>
									<td>
										<a
											@click="deleteTargetFromTree(target.id)"
											class="btn btn-list-function"
											v-tooltip.right="$t('PING_TREE_LIST_DELETE_TT')"
										>
											<i class="typcn typcn-trash"></i>
										</a>
										<i
											v-if="!isPTTargetFree(target)"
											class="f-s-20 p-r-8 typcn bright-red typcn-warning"
											v-tooltip.top="$t('PING_TREE_TARGET_DUPLICATED_WARNING')"
										></i>
									</td>
								</tr>
							</template>
						</tbody>
					</v-table>
				</div>
				<!-- Add Ring Tree Targets End -->

				<!-- Form Actions ( create/update, cancel ) -->
				<div class="form-group" data-testid="submit-wrapper">
					<div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">
						<p v-if="invalid && triedToSend" class="m-t-8 m-b-8 bright-red">
							<i class="f-s-20 p-r-0 typcn typcn-warning" />{{
								$t('INVALID_FORM')
							}}
						</p>
						<PrimaryButton
							size="sm"
							:show-loader-on-form="showLoader"
							data-testid="submit"
							@click="triedToSend = true"
						>
							<span>{{ currentRingTree.id ? 'UPDATE RING TREE' : 'CREATE RING TREE'}}</span>
						</PrimaryButton>

						<button
							v-t="'CANCEL'"
							class="btn btn-sm btn-danger m-l-15"
							@click="cancel"
							data-testid="cancel"
						/>
					</div>
				</div>
			</fieldset>
		</form>
	</ValidationObserver>
</template>

<script>
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import Field from '@/core/components/LabelAndElement/LabelAndElement.vue';
import NzToggle from '@/core/components/nzToggle/nzToggle';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle.vue';
import PrimaryButton from '@/core/components/PrimaryButton';

import messages from '../locales';
import sharedMessages from '@/core/locales';

import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import { STATUS_ERROR } from '@/store/helpers';
import * as actionTypes from '@/modules/ringTree/store/actionTypes';
import HoursOfOperation from '@/core/components/HoursOfOperation/index.vue';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { pingTreeTargetService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
const timeZoneConfig = {
	reduce: (option) => option.id,
	label: 'display'
};

export default {
	name: 'RingTreeForm',
	components: {
		Field,
		PrimaryButton,
		NzToggle,
		MultipleToggle,
		SearchableDropdown,
		HoursOfOperation
	},
	props: {
		ringTree: {
			type: Object,
			default: () => ({})
		},
		showLoader: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		currentRingTree: {
			name: '',
			schedule: {
				timeZoneId: null,
				hoursOfOperation: null
			},
			revenueSettings: {
				revenueDynamic: true,
				revenueOnFailure: null,
				conversionCallLengthDynamic: false,
				maxConversionCallDurationSeconds: null,
				conversionParams: {
					conversionType: 'connectedCall',
					conversionArgs: {
						startTimerEvent: '',
						callLengthInSeconds: null
					}
				}
			},
			errorSettings: {
				callOnFailure: false,
				dialTimeoutSeconds: null,
				pingTimeoutInMS: null
			},
			convertOnSettings: {},
			staticRevenueAmount: 0,
			minRevenueToRoute: null,
			targets: []
		},
		ringTreeTargets: [],
		selectedTarget: null,
		targetInCurrentTree: false,
		hoursOfOperationEnabled: false,
		triedToSend: false
	}),
	created() {
		if (!this.timezones?.length) {
			this.getTimezones();
		}
		this.loadTargets();
	},
	watch: {
		selectedTarget: function(newVal) {
			if (newVal) {
				this.targetInCurrentTree = this.currentRingTree.targets.some(
					(target) => target.id === newVal.id
				);
			}
		},
		ringTree: {
			deep: true,
			immediate: true,
			handler(newVal) {
				if (newVal && newVal.id) {
					this.currentRingTree = _.cloneDeep(newVal);
					const revenueSettings = this.currentRingTree.revenueSettings;
					if (
						revenueSettings.conversionParams &&
						revenueSettings.conversionParams.conversionType
					) {
						if (!revenueSettings.conversionParams.conversionArgs) {
							revenueSettings.conversionParams.conversionArgs = {
								startTimerEvent: '',
								callLengthInSeconds: ''
							};
						}
					}
					this.ringTreeTargets = _.cloneDeep(this.currentRingTree.targets);
					this.hoursOfOperationEnabled = !!this.currentRingTree.schedule
						.hoursOfOperation;
				}
			}
		}
	},
	computed: {
		...mapState(['timezones']),
		...mapState({
			accountId: 'currentAccountId'
		}),
		timeZoneOptions() {
			return {
				options: this.timezones,
				...timeZoneConfig,
				placeholder: 'Select Timezone'
			};
		},
		hasRingTreeTargets: function () {
      return this.currentRingTree.targets.length
    },
		...mapState('ringTrees', ['currentTargets']),
		...mapGetters('ringTrees', [
			'createRingTreeStatus',
			'updateRingTreeStatus',
			'getRingTreeTargetsStatus'
		]),
		revenueTypeOptions() {
			return [
				{ name: this.$t('PING_TREE_REVENUE_DYNAMIC'), value: true },
				{ name: this.$t('PING_TREE_REVENUE_STATIC'), value: false }
			];
		},
		revenueConvertOnSettingConfig() {
			return {
				options: [
					{ name: this.$t('PING_TREE_CONVERT_LENGTH'), value: 'callLength' },
					{
						name: this.$t('PING_TREE_CALL_SUCCESSFULLY_CONNECTED'),
						value: 'connectedCall'
					},
					{ name: this.$t('PING_TREE_CONVERT_POSTBACK'), value: 'postBack' },
					{ name: this.$t('PING_TREE_CONVERT_ON_DIALED'), value: 'dialed' }
				],
				reduce: (option) => option.value,
				placeholder: 'Choose Type',
				label: 'name',
				rules: 'required'
			};
		},
		computedConversionType() {
			return this.currentRingTree.revenueSettings.conversionParams
				.conversionType
				? this.currentRingTree.revenueSettings.conversionParams.conversionType
				: '';
		},
		startCallLengthOnOptions() {
			return [
				{ name: this.$t('PING_TREE_START_INCOMING'), value: 'OnIncomingCall' },
				{ name: this.$t('PING_TREE_START_DIAL'), value: 'OnDial' },
				{ name: this.$t('PING_TREE_START_CONNECT'), value: 'OnConnectedCall' }
			];
		},
		filteredTargets() {
			return this.currentTargets.filter(
				(target) =>
					!this.currentRingTree.targets.some((t) => t.id === target.id)
			);
		},
		targetsConfig() {
			return {
				options: this.filteredTargets,
				label: 'name',
				placeholder: 'Select Target'
			};
		},
		computedHoursOfOperation() {
			return this.currentRingTree.schedule?.hoursOfOperation || null;
		},
		conversionCallLengthValueOptions() {
			return [
				{ name: this.$t('PING_TREE_REVENUE_DYNAMIC'), value: true },
				{ name: this.$t('PING_TREE_TARGET_REVENUE_STATIC'), value: false }
			];
		}
	},
	methods: {
		...mapActions(['getTimezones']),
		...mapActions('ringTrees', Object.values(actionTypes)),
    ...mapMutations('ringTrees',['setActionStatusPending','setActionStatusError']),
		updateRingTreeHoursOfOperation(updatedHoursOfOperation) {
			if (updatedHoursOfOperation) {
				this.currentRingTree.schedule.hoursOfOperation = updatedHoursOfOperation;
			} else {
				this.currentRingTree.schedule.hoursOfOperation = null;
			}
		},
		onRevenueTypeSettingToggled({ value }) {
			this.currentRingTree.revenueSettings.revenueDynamic = value;
		},
		startCallLengthOnToggled({ value }) {
			this.currentRingTree.revenueSettings.conversionParams.conversionArgs.startTimerEvent = value;
		},
		conversionCallLengthValueToggled({ value }) {
			this.currentRingTree.revenueSettings.conversionCallLengthDynamic = value;
		},
		async loadTargets() {
			await this.getRingTreeTargets();
			if (this.getRingTreeTargetsStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.getRingTreeTargetsStatus.error);
			}
		},
		addTargetToTree() {
			this.currentRingTree.targets.push(this.selectedTarget);
			this.selectedTarget = null;
		},
		navigateToTarget(targetId) {
			return $ngStateService.href('dashboard.ringtreetarget.edit', {
				pingTreeTargetId: targetId
			});
		},
		deleteTargetFromTree(targetId) {
			this.currentRingTree.targets.splice(
				_.findIndex(this.currentRingTree.targets, { id: targetId }),
				1
			);
		},
		isPTTargetFree(target) {
			return !(
				target.pingTreeId && target.pingTreeId !== this.currentRingTree.id
			);
		},
		_resetHours() {
			if (!this.hoursOfOperationEnabled) {
				this.currentRingTree.schedule.hoursOfOperation = null;
			}
		},
		async clonePTT() {
			if (this.currentRingTree.targets) {
				for (
					let index = 0;
					index < this.currentRingTree.targets.length;
					index++
				) {
					const ringTreeTarget = this.currentRingTree.targets[index];
					if (!this.isPTTargetFree(ringTreeTarget)) {
							// Await the asynchronous operation to complete before continuing to the next iteration
							const newTarget = await pingTreeTargetService.duplicateTarget(this.accountId, ringTreeTarget.id).then(async response => {
								const target = response.target;
								target.name = ringTreeTarget.name + ' - ' + this.currentRingTree.name
								target.schedule = ringTreeTarget.schedule;
								const formattedTarget = pingTreeTargetService.formatParsers(target);
								const createdTarget = await pingTreeTargetService.createTarget(this.accountId, formattedTarget);
								return createdTarget;
							})

							//Link Buyer
							if (ringTreeTarget?.owner?.id) {
								await pingTreeTargetService.addBuyer(this.accountId, newTarget.data.pingTreeTarget.id, ringTreeTarget.owner.id);
							}
							//Link Auth Connection
							if (ringTreeTarget?.authConnection?.id) {
								await pingTreeTargetService.linkToItem(this.accountId, newTarget.data.pingTreeTarget.id, {
								memberName: 'AuthConnection',
								memberId: ringTreeTarget.authConnection.id
								});
							}
							// Link RPC
							if (ringTreeTarget?.rpc?.id && !(ringTreeTarget?.rpc?.id?.substr(0, 2) === 'PI' && ringTreeTarget?.rpc?.id?.substr(-4) === 'LINK')) {
								await pingTreeTargetService.linkToItem(this.accountId, newTarget.data.pingTreeTarget.id, {
										memberName: 'RPC',
										memberId: ringTreeTarget.rpc.id
									});
							}

							// Sets the newly created Ring Tree Target and overwrites the Ring Tree Target that is being copied.
							this.$set(
								this.currentRingTree.targets,
								index,
								newTarget.data.pingTreeTarget
							);
						}
				}
			}
		},
		async onSubmit() {
			if (!this.triedToSend) return;
			this._resetHours();
			try {
				// Trigger the pending state for create or update Ring Tree to make the spinner appear on the button immediately before the async actions.
				this.triggerStatus('Pending');
				
				await this.clonePTT();
				const newTargets = this.currentRingTree.targets.filter(
					(target) =>
						!this.ringTreeTargets.some((rTarget) => rTarget.id === target.id)
				);
				const removedTargets = this.ringTreeTargets.filter(
					(cTarget) =>
						!this.currentRingTree.targets.some(
							(target) => target.id === cTarget.id
						)
				);
				const payload = {
					ringTree: this.currentRingTree,
					newTargets,
					removedTargets
				};
				if (!this.currentRingTree.id) {
					payload.ringTree.doNotCreateUser = true;
				}
				this.triedToSend = false;
				this.$emit('submit', payload);
			} catch (error) {
				// Clear the pending status and set to error state if an error occurs.
				this.triggerStatus('Error');
				utilities.errorHandler(error);
			}
		},
		triggerStatus(status) {
			const actionName = this.currentRingTree.id ? 'updateRingTree' : 'createRingTree';
			this[`setActionStatus${status}`]({ actionName });
		},
		cancel(evt) {
			evt.preventDefault();
			this.$emit(
				'cancel',
				!this.currentRingTree.id ? null : this.currentRingTree
			);
		}
	},
	i18n: {
		messages,
		sharedMessages,
		locale: 'en'
	}
};
</script>
